import { DocumentCard } from '@dealstack/frontend/shared/components/DocumentCard/DocumentCard';
import { ExtensionSquareIcon } from '@dealstack/frontend/shared/components/DocumentCard/Components/SquareTextIcons';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { SubjectAction } from '@dealstack/shared/lib/generated';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { Button, Flex, Text } from '@mantine/core';
import { DocumentViewerModalV2 } from '@dealstack/frontend/shared/components/DocumentViewerV2/DocumentViewerModalV2';
import { useDownloadedFileBlob } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadedFileBlob';
import { type ReactNode, useState } from 'react';
import { useAppSelector } from '@dealstack/frontend/investor/hooks/redux';
import { ExtensionIconBox, ProofFileBoxWrapper } from './styled.ts';

interface Props {
	fileId: string;
	fileName: string;
	onDelete: () => void;
	onConfirm?: () => void;
	isRejected?: boolean;
	rejectionComment?: string;
	date?: string;
	dataTestId?: string;
	subjectAction?: SubjectAction | null;
	/** DC-825: the proof's certification status and actions, rendered under the card. */
	certificationSlot?: ReactNode;
}

export const ProofFileBox = ({
	fileName,
	fileId,
	isRejected,
	onDelete,
	onConfirm,
	rejectionComment,
	date,
	dataTestId,
	subjectAction,
	certificationSlot,
}: Props) => {
	const needsConfirmation = subjectAction === SubjectAction.NOT_YET_ACTED;
	const tInvestor = useTranslationInvestor();
	const [showPreviewDocumentModal, setShowPreviewDocumentModal] = useState(false);
	const dealId = useAppSelector((state) => state.user.dealId);
	const { data: file, isLoading } = useDownloadedFileBlob({ fileId, dealId: dealId as string });

	return (
		<ProofFileBoxWrapper data-testid={dataTestId}>
			<DocumentCard
				documentTitle={fileName}
				documentID={fileId}
				date={formatStringDateToUI(date)}
				leftIcon={
					isRejected ? (
						<Icon variant="warning" color={defaultTheme.v3.colors.blue[6]} />
					) : (
						<ExtensionIconBox>
							<ExtensionSquareIcon extension={fileName.split('.').pop()} />
						</ExtensionIconBox>
					)
				}
				onClickCard={() => setShowPreviewDocumentModal(true)}
				isUploaded
				isWarning={isRejected}
			/>
			{certificationSlot}
			{needsConfirmation && (
				<Flex gap="0.5rem" mt="0.5rem">
					<Button
						size="sm"
						variant="outline"
						color="dark"
						onClick={onDelete}
						data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_INHERITED_DISMISS_BUTTON}
					>
						{tInvestor('investment.join.kyc.inherited.no')}
					</Button>
					<Button
						size="sm"
						variant="filled"
						color="blue"
						onClick={onConfirm}
						leftSection={<Icon variant="tick" size="1rem" />}
						data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_INHERITED_CONFIRM_BUTTON}
					>
						{tInvestor('investment.join.kyc.inherited.yes')}
					</Button>
				</Flex>
			)}
			{isRejected && (
				<Flex direction="column" gap="0.5rem" mt="0.5rem">
					<Text c="red.6">
						{tInvestor('investment.join.kyc.proof_box.rejected')}
						{rejectionComment &&
							tInvestor('investment.join.kyc.proof_box.rejected_comment', {
								rejectionComment,
							})}
					</Text>
					<Flex>
						<Button
							size="sm"
							variant="outline"
							color="red"
							onClick={onDelete}
							data-testid={
								INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_REJECTED_FILE_REMOVE_BUTTON
							}
						>
							{tInvestor('investment.join.kyc.proof_box.remove')}
						</Button>
					</Flex>
				</Flex>
			)}
			{showPreviewDocumentModal ? (
				<DocumentViewerModalV2
					opened={showPreviewDocumentModal}
					fileName={fileName}
					file={file?.blob}
					fileExtension={file?.fileExtension}
					isLoading={isLoading}
					onClose={() => setShowPreviewDocumentModal(false)}
				/>
			) : null}
		</ProofFileBoxWrapper>
	);
};
